/**
 * @omdxp/jslog - Structured logging for Node.js
 *
 * A high-performance structured logging library inspired by Go's log/slog,
 * with additional features specifically designed for Node.js applications.
 *
 * @packageDocumentation
 *
 * @example Basic usage
 * ```typescript
 * import { Logger, TextHandler, Level, Int, String } from '@omdxp/jslog';
 *
 * const logger = new Logger(new TextHandler({ level: Level.INFO }));
 * logger.info("Server started", Int("port", 3000), String("env", "production"));
 * ```
 *
 * @example With file rotation
 * ```typescript
 * import { Logger, FileHandler } from '@omdxp/jslog';
 *
 * const logger = new Logger(new FileHandler({
 *   filepath: './logs/app.log',
 *   maxSize: 10 * 1024 * 1024,  // 10MB
 *   maxFiles: 5
 * }));
 * ```
 *
 * @example Async non-blocking logging
 * ```typescript
 * import { Logger, AsyncHandler, JSONHandler } from '@omdxp/jslog';
 *
 * const logger = new Logger(new AsyncHandler({
 *   handler: new JSONHandler()
 * }));
 * logger.info("This never blocks");
 * ```
 */

import type { LogContext } from "./utils";

// Core types and interfaces

// Core types and classes
export {
  Logger,
  Level,
  LevelVar,
  type Attr,
  type Value,
  type Record,
  type Handler,
  type Source,
  type LogValuer,
} from "./logger";

// Standard handlers
export {
  TextHandler,
  JSONHandler,
  DiscardHandler,
  MultiHandler,
  type HandlerOptions,
} from "./handlers";

// Advanced handlers (Go slog wishes it had these!)
export {
  FileHandler,
  BufferedHandler,
  SamplingHandler,
  FilterHandler,
  ColorHandler,
  PrettyHandler,
  type FileHandlerOptions,
  type BufferedHandlerOptions,
  type SamplingHandlerOptions,
  type FilterHandlerOptions,
  type ColorHandlerOptions,
  type PrettyHandlerOptions,
} from "./advanced-handlers";

// Async & Middleware support (composition FTW!)
export {
  AsyncHandler,
  MiddlewareHandler,
  MetricsMiddleware,
  type AsyncHandlerOptions,
  type MiddlewareHandlerOptions,
  type HandlerMiddleware,
  // Middleware functions
  timestampMiddleware,
  hostnameMiddleware,
  pidMiddleware,
  rateLimitMiddleware,
  dedupeMiddleware,
  enrichMiddleware,
  transformMiddleware,
  conditionalMiddleware,
  errorBoundaryMiddleware,
} from "./middleware";

// Utility functions (Go slog can't compete!)
export {
  Timer,
  startTimer,
  LogContext,
  AttrBuilder,
  attrs,
  setCorrelationId,
  getCorrelationId,
  clearCorrelationId,
  CorrelationId,
  generateRequestId,
  generateTraceId,
  safeStringify,
  lazy,
  redact,
  maskEmail,
  maskCreditCard,
  maskPhone,
  EnvInfo,
  MemoryUsage,
  HttpReq,
  HttpRes,
  SqlQuery,
  StackTrace,
  Caller,
  type HttpRequest,
  type HttpResponse,
} from "./utils";

// Attribute helpers
export {
  String,
  Int,
  Int64,
  Uint64,
  Float64,
  Bool,
  Time,
  Duration,
  Any,
  Group,
  Err,
} from "./logger";

import { Logger, Level, Attr, Handler } from "./logger";
import { TextHandler } from "./handlers";

// Default logger instance (similar to Go's slog.Default())
let defaultLogger: Logger | null = null;

/**
 * Get or create the default logger
 */
export function Default(): Logger {
  if (!defaultLogger) {
    defaultLogger = new Logger(new TextHandler({ level: Level.INFO }));
  }
  return defaultLogger;
}

/**
 * Set the default logger
 */
export function SetDefault(logger: Logger): void {
  defaultLogger = logger;
}

/**
 * Create a new logger with the given handler
 */
export function New(handler: Handler): Logger {
  return new Logger(handler);
}

// Convenience functions using the default logger

export function debug(msg: string, ...attrs: Attr[]): void {
  Default().debug(msg, ...attrs);
}

export function debugContext(
  msg: string,
  ctx: LogContext,
  ...attrs: Attr[]
): void {
  Default().debugContext(msg, ctx, ...attrs);
}

export function info(msg: string, ...attrs: Attr[]): void {
  Default().info(msg, ...attrs);
}

export function infoContext(
  msg: string,
  ctx: LogContext,
  ...attrs: Attr[]
): void {
  Default().infoContext(msg, ctx, ...attrs);
}

export function warn(msg: string, ...attrs: Attr[]): void {
  Default().warn(msg, ...attrs);
}

export function warnContext(
  msg: string,
  ctx: LogContext,
  ...attrs: Attr[]
): void {
  Default().warnContext(msg, ctx, ...attrs);
}

export function error(msg: string, ...attrs: Attr[]): void {
  Default().error(msg, ...attrs);
}

export function errorContext(
  msg: string,
  ctx: LogContext,
  ...attrs: Attr[]
): void {
  Default().errorContext(msg, ctx, ...attrs);
}

export function log(level: Level, msg: string, ...attrs: Attr[]): void {
  Default().log(level, msg, ...attrs);
}

export function logAttrs(level: Level, msg: string, ...attrs: Attr[]): void {
  Default().logAttrs(level, msg, ...attrs);
}

/**
 * With returns a logger that includes the given attributes
 */
export function with_(...attrs: Attr[]): Logger {
  return Default().with(...attrs);
}

/**
 * WithGroup returns a logger that starts a group
 */
export function withGroup(name: string): Logger {
  return Default().withGroup(name);
}
